import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';
import { useAuthStore } from 'stores/authStore';
import { clientAuthorizedGet, clientAuthorizedPost } from 'utils/fetchUtils';
import { API_ROUTES, CACHE_KEYS, ROUTES } from 'utils/constants';
import { UserProfile } from 'types/user';
import { useLocalStorage } from 'hooks/useLocalStorage';

const { USER_PROFILE: USER_PROFILE_CACHE_KEY } = CACHE_KEYS;

/** Cache TTL for profile picture download URL (signed URLs expire; 50 min is safe). */
const PROFILE_PICTURE_URL_CACHE_MS = 50 * 60 * 1000;

const hasCompany = (userProfile: UserProfile | null): boolean => {
  return Boolean(userProfile?.companies && userProfile.companies.length > 0);
};

interface CacheRef {
  setCachedProfile: (profile: UserProfile) => Promise<void>;
  removeCachedProfile: () => void;
}

interface UserProfileStore {
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  isProfileNotFound: boolean;
  hasTriggeredCacheRefresh: boolean;
  cacheRef: CacheRef | null;
  /** Cached signed URL for profile picture; invalidate when expired or profile has no picture. */
  profilePictureUrl: string | null;
  profilePictureUrlExpiresAt: number;
  /** Path that the current cached URL is for; invalidate cache when profile path changes. */
  profilePicturePathForCachedUrl: string | null;
  setCacheRef: (ref: CacheRef | null) => void;
  setProfileFromCache: (profile: UserProfile | null) => void;
  clearForLogout: () => void;
  setHasTriggeredCacheRefresh: (value: boolean) => void;
  /** Fetches and caches profile picture download URL; returns cached URL if still valid. */
  getProfilePictureDownloadUrl: () => Promise<string | null>;
  /** Set profile picture URL (e.g. after upload); expiresAt defaults to now + cache TTL. */
  setProfilePictureUrl: (url: string | null, expiresAt?: number) => void;
  refreshProfile: (silent?: boolean) => Promise<void>;
  createProfile: (name: string) => Promise<UserProfile>;
}

export const useUserProfileStore = create<UserProfileStore>((set, get) => ({
  profile: null,
  isLoading: true,
  error: null,
  isProfileNotFound: false,
  hasTriggeredCacheRefresh: false,
  cacheRef: null,
  profilePictureUrl: null,
  profilePictureUrlExpiresAt: 0,
  profilePicturePathForCachedUrl: null,

  setCacheRef: (ref) => set({ cacheRef: ref }),

  setProfileFromCache: (profile) => {
    set({ profile, isLoading: false });
    if (!profile?.profilePicturePath) {
      set({
        profilePictureUrl: null,
        profilePictureUrlExpiresAt: 0,
        profilePicturePathForCachedUrl: null,
      });
    }
  },

  clearForLogout: () =>
    set({
      profile: null,
      isLoading: false,
      error: null,
      isProfileNotFound: false,
      hasTriggeredCacheRefresh: false,
      profilePictureUrl: null,
      profilePictureUrlExpiresAt: 0,
      profilePicturePathForCachedUrl: null,
    }),

  getProfilePictureDownloadUrl: async () => {
    const {
      profile,
      profilePictureUrl,
      profilePictureUrlExpiresAt,
      profilePicturePathForCachedUrl,
    } = get();
    if (!profile?.profilePicturePath) {
      set({
        profilePictureUrl: null,
        profilePictureUrlExpiresAt: 0,
        profilePicturePathForCachedUrl: null,
      });
      return null;
    }
    const now = Date.now();
    const cacheStillValid =
      profilePictureUrl &&
      profilePictureUrlExpiresAt > now &&
      profilePicturePathForCachedUrl === profile.profilePicturePath;
    if (cacheStillValid) {
      return profilePictureUrl;
    }
    try {
      const res = await clientAuthorizedGet<{ downloadUrl: string }>(
        API_ROUTES.USER.PROFILE_PICTURE_DOWNLOAD_URL,
      );
      const expiresAt = now + PROFILE_PICTURE_URL_CACHE_MS;
      set({
        profilePictureUrl: res.downloadUrl,
        profilePictureUrlExpiresAt: expiresAt,
        profilePicturePathForCachedUrl: profile.profilePicturePath,
      });
      return res.downloadUrl;
    } catch {
      set({
        profilePictureUrl: null,
        profilePictureUrlExpiresAt: 0,
        profilePicturePathForCachedUrl: null,
      });
      return null;
    }
  },

  setProfilePictureUrl: (url, expiresAt) => {
    const { profile } = get();
    const expires = expiresAt ?? Date.now() + PROFILE_PICTURE_URL_CACHE_MS;
    set({
      profilePictureUrl: url,
      profilePictureUrlExpiresAt: url ? expires : 0,
      profilePicturePathForCachedUrl: url
        ? (profile?.profilePicturePath ?? null)
        : null,
    });
  },

  setHasTriggeredCacheRefresh: (value) =>
    set({ hasTriggeredCacheRefresh: value }),

  refreshProfile: async (silent = false) => {
    const { isAuth, isLoading: isAuthLoading } = useAuthStore.getState();
    const { cacheRef } = get();

    if (isAuthLoading) return;
    if (!isAuth) {
      set({ profile: null, isLoading: false });
      cacheRef?.removeCachedProfile();
      return;
    }

    try {
      if (!silent) set({ isLoading: true });
      set({ error: null, isProfileNotFound: false });
      const response = await clientAuthorizedGet<
        UserProfile | { message: string }
      >(API_ROUTES.USER.PROFILE);

      if (
        response &&
        typeof response === 'object' &&
        'message' in response &&
        response.message === 'Profile not found'
      ) {
        set({
          profile: null,
          isProfileNotFound: true,
          error: 'Profile not found',
        });
        cacheRef?.removeCachedProfile();
      } else {
        const resolvedProfile = response as UserProfile;
        const currentCachedPath = get().profilePicturePathForCachedUrl;
        const pathChanged =
          resolvedProfile.profilePicturePath !== currentCachedPath;
        const clearPictureCache =
          !resolvedProfile.profilePicturePath || pathChanged;
        set({
          profile: resolvedProfile,
          isProfileNotFound: false,
          ...(clearPictureCache && {
            profilePictureUrl: null,
            profilePictureUrlExpiresAt: 0,
            profilePicturePathForCachedUrl: null,
          }),
        });
        if (hasCompany(resolvedProfile) && cacheRef) {
          await cacheRef.setCachedProfile(resolvedProfile);
        } else {
          cacheRef?.removeCachedProfile();
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load user profile';
      set({ error: errorMessage, isProfileNotFound: false });
    } finally {
      if (!silent) set({ isLoading: false });
    }
  },

  createProfile: async (name: string): Promise<UserProfile> => {
    const { cacheRef } = get();
    try {
      set({ error: null });
      const newProfile = await clientAuthorizedPost<UserProfile>(
        API_ROUTES.USER.PROFILE,
        { name } as Record<string, unknown>,
      );
      set({ profile: newProfile, isProfileNotFound: false });
      if (hasCompany(newProfile) && cacheRef) {
        await cacheRef.setCachedProfile(newProfile);
      } else {
        cacheRef?.removeCachedProfile();
      }
      return newProfile;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create user profile';
      set({ error: errorMessage });
      throw err;
    }
  },
}));

/** True when: user is authenticated, loading is done, and profile was confirmed not to exist (404). */
const isProfileNotFoundForAuthenticatedUser = (params: {
  isAuth: boolean;
  isAuthLoading: boolean;
  isProfileLoading: boolean;
  isProfileNotFound: boolean;
  profile: UserProfile | null;
}): boolean =>
  params.isAuth &&
  !params.isAuthLoading &&
  !params.isProfileLoading &&
  params.isProfileNotFound &&
  params.profile === null;

export function useUserProfileInit(): void {
  const isAuth = useAuthStore((s) => s.isAuth);
  const isAuthLoading = useAuthStore((s) => s.isLoading);
  const {
    data: cachedProfile,
    setData: setCachedProfile,
    removeData: removeCachedProfile,
    isInitialized: isCacheInitialized,
  } = useLocalStorage<UserProfile>({
    key: USER_PROFILE_CACHE_KEY,
    encrypt: true,
  });

  const setCacheRef = useUserProfileStore((s) => s.setCacheRef);
  const setProfileFromCache = useUserProfileStore((s) => s.setProfileFromCache);
  const clearForLogout = useUserProfileStore((s) => s.clearForLogout);
  const setHasTriggeredCacheRefresh = useUserProfileStore(
    (s) => s.setHasTriggeredCacheRefresh,
  );
  const refreshProfile = useUserProfileStore((s) => s.refreshProfile);
  const hasTriggeredCacheRefresh = useUserProfileStore(
    (s) => s.hasTriggeredCacheRefresh,
  );
  const profile = useUserProfileStore((s) => s.profile);
  const isLoading = useUserProfileStore((s) => s.isLoading);
  const isProfileNotFound = useUserProfileStore((s) => s.isProfileNotFound);
  const navigate = useNavigate();

  useEffect(() => {
    setCacheRef({
      setCachedProfile,
      removeCachedProfile,
    });
    return () => setCacheRef(null);
  }, [setCachedProfile, removeCachedProfile, setCacheRef]);

  useEffect(() => {
    if (!isCacheInitialized) return;

    if (!isAuth && !isAuthLoading) {
      clearForLogout();
      removeCachedProfile();
      return;
    }

    if (cachedProfile && hasCompany(cachedProfile)) {
      setProfileFromCache(cachedProfile);
      if (!hasTriggeredCacheRefresh) {
        setHasTriggeredCacheRefresh(true);
        void refreshProfile(true);
      }
    } else if (!cachedProfile && !isAuth && !isAuthLoading) {
      setProfileFromCache(null);
    } else if (isAuth && !isAuthLoading && !cachedProfile) {
      void refreshProfile();
    }
  }, [
    isCacheInitialized,
    isAuth,
    isAuthLoading,
    cachedProfile,
    hasTriggeredCacheRefresh,
    setProfileFromCache,
    setHasTriggeredCacheRefresh,
    refreshProfile,
    clearForLogout,
    removeCachedProfile,
  ]);

  useEffect(() => {
    if (
      isProfileNotFoundForAuthenticatedUser({
        isAuth,
        isAuthLoading,
        isProfileLoading: isLoading,
        isProfileNotFound,
        profile,
      })
    ) {
      navigate(ROUTES.USER.COMPLETE_PROFILE);
    }
  }, [isAuth, isAuthLoading, isLoading, isProfileNotFound, profile, navigate]);
}
